import React, {Component} from 'react'
import moment from 'moment-timezone'
import Tippy from '@tippyjs/react'
import {
    ArrowPathIcon,
    ChevronDownIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline'
import {DocumentIcon} from '@heroicons/react/20/solid'
import DocumentArrowDownIcon from '@heroicons/react/20/solid/DocumentArrowDownIcon'

import {getResource} from '../../../data/actions/resource'
import {download} from '../../../data/actions/download'
import LocalStorage from '../../../util/localStorage'
import Resources from '../../../data/services/resources'
import {Field, FieldsManager} from '../../../data/services/fields'
import {classNames, getProp} from '../../../common/util/util-helpers'
import {genericMoneyFormatter} from '../../../common/util/util-vanilla'
import {currentDate} from '../../../common/util/util-dates'
import {fieldsToHtml} from '../../../common/util/util-fields'
import {DEFAULT_DATABASE_DATETIME_FORMAT} from '../../../common/util/util-consts'
import {LoaderSmall} from '../../../common/components/loader'
import PageHeader from '../../../common/components/layout/layout-components/page/page-header'
import Card from '../../../common/components/card'

export default class CashflowTab extends Component {
    constructor(props) {
        super(props)
        this.state = {
            queryFilterFields: this.getQueryFilterFields(),
            collapseOperating: false,
            collapseInvesting: false,
            collapseFinancing: false,
        }
    }

    /** Lifecycle
     ================================================================= */
    componentDidMount = () => {
        this.fetchData()
    }

    /** Data Events
     ================================================================= */
    fetchData = () => {
        this.props.dispatch(getResource({
            user: LocalStorage.get('user'),
            resource: Resources.AccountingCashflow,
            query: this.getQuery()
        }))
    }

    downloadExcel = () => {
        this.props.dispatch(download({
            user: LocalStorage.get('user'),
            resource: Resources.AccountingCashflow,
            query: Object.assign({
                format: 'EXCEL',
                name: 'accounting_cashflow_' + currentDate() + '.xlsx',
            }, this.getQuery())
        }))
    }

    downloadPdf = () => {
        this.props.dispatch(download({
            user: LocalStorage.get('user'),
            resource: Resources.AccountingCashflow,
            query: Object.assign({
                format: 'PDF',
                name: 'accounting_cashflow_' + currentDate() + '.pdf',
            }, this.getQuery())
        }))
    }

    /** UI Events
     ================================================================= */
    handleInputChange = (name, value) => {
        this.setState(
            {queryFilterFields: FieldsManager.updateField(this.state.queryFilterFields, name, value)},
            this.fetchData
        )
    }

    /** Helpers
     ================================================================= */
    getQuery = () => {
        return FieldsManager.getFieldKeyValues(this.state.queryFilterFields)
    }

    getQueryFilterFields = () => {
        return {
            StartDate: new Field('StartDate', moment().startOf('year').format(DEFAULT_DATABASE_DATETIME_FORMAT), ['date'], false, 'date', {
                addContainerClass: 'col-span-4'
            }),
            EndDate: new Field('EndDate', moment().format(DEFAULT_DATABASE_DATETIME_FORMAT), ['date'], false, 'date', {
                addContainerClass: 'col-span-4'
            })
        }
    }

    formatMoney = (value) => genericMoneyFormatter(value)

    /** Render
     ================================================================= */
    renderItemRow = (item, idx) => (
        <div key={idx} className="flex items-center justify-between p-2 pl-12 hover:bg-tm-gray-300">
            <div className="text-sm text-tm-gray-900">{item.Name}</div>
            <div className="text-sm text-tm-gray-900">{this.formatMoney(item.Value ?? 0)}</div>
        </div>
    )

    renderSectionHeader = (label, collapseKey) => (
        <h3
            onClick={() => this.setState({[collapseKey]: !this.state[collapseKey]})}
            className="hover:bg-tm-gray-300 p-2 text-lg leading-6 font-medium text-tm-gray-900 flex justify-start items-center cursor-pointer"
        >
            {!this.state[collapseKey]
                ? <ChevronDownIcon className="h-6 w-6 mr-3"/>
                : <ChevronRightIcon className="h-6 w-6 mr-3"/>}
            {label}
        </h3>
    )

    renderSectionTotal = (label, value) => (
        <div className="flex items-center justify-between p-2 pl-5 font-bold border-t border-tm-gray-300">
            <div>{label}</div>
            <div>{this.formatMoney(value ?? 0)}</div>
        </div>
    )

    render() {
        const {translate} = this.props
        const data = getProp(this.props, 'resource.data', {}) || {}
        const Operating = data.Operating ?? {NetIncome: 0, Adjustments: [], TotalAdjustments: 0, NetCash: 0}
        const Investing = data.Investing ?? {Items: [], NetCash: 0}
        const Financing = data.Financing ?? {Items: [], NetCash: 0}

        const fieldsHtml = fieldsToHtml(
            Object.values(Object.assign({}, this.state.queryFilterFields)),
            translate,
            this.handleInputChange
        )

        const StartDateLabel = moment(this.state.queryFilterFields.StartDate.value, DEFAULT_DATABASE_DATETIME_FORMAT).format('MMMM Do YYYY')
        const EndDateLabel = moment(this.state.queryFilterFields.EndDate.value, DEFAULT_DATABASE_DATETIME_FORMAT).format('MMMM Do YYYY')

        return (
            <Card>
                <div className="mb-5">
                    <PageHeader title={translate('page.heading.CashflowStatement') ?? 'Statement of Cash Flows'}>
                        <div className="flex items-center space-x-2">
                            <Tippy content={translate('text.download_excel')}>
                                <button className="btn-icon" onClick={this.downloadExcel}>
                                    <DocumentArrowDownIcon className="w-5 h-5"/>
                                </button>
                            </Tippy>

                            <Tippy content={translate('text.download_pdf')}>
                                <button className="btn-icon" onClick={this.downloadPdf}>
                                    <DocumentIcon className="w-5 h-5"/>
                                </button>
                            </Tippy>

                            <button className={classNames('btn btn-header z-10 ml-8')} onClick={this.fetchData}>
                                <ArrowPathIcon className={classNames(
                                    'w-5 h-5',
                                    this.props.resource?.isLoading ? 'animate-spin' : undefined
                                )}/>
                            </button>
                        </div>
                    </PageHeader>
                </div>

                <div className="row mb-4">
                    <div className="col p-5 grid grid-cols-12 gap-4">
                        {fieldsHtml}
                    </div>
                </div>

                {/* HEADER */}
                <div className="text-center">
                    <h1 className="font-bold">{translate('text.STATEMENT_OF_CASH_FLOWS') ?? 'STATEMENT OF CASH FLOWS'}</h1>
                    <h2>{StartDateLabel} &mdash; {EndDateLabel}</h2>
                </div>

                <div className="flex items-center justify-between border-b border-gray-300">
                    <div className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-tm-gray-500"/>
                    <div className="relative py-3.5 pl-3 pr-4 sm:pr-6 text-right font-bold">
                        {translate('text.Total')}
                    </div>
                </div>

                {/* OPERATING ACTIVITIES */}
                <div className="bg-inverse">
                    {this.renderSectionHeader(translate('text.OperatingActivities') ?? 'Operating Activities', 'collapseOperating')}
                    {!this.state.collapseOperating && (
                        <>
                            <div className="flex items-center justify-between p-2 pl-7 font-bold">
                                <div>{translate('text.NetIncome')}</div>
                                <div>{this.formatMoney(Operating.NetIncome ?? 0)}</div>
                            </div>
                            <div className="p-2 pl-7 italic text-sm text-tm-gray-700">
                                {translate('text.AdjustmentsToReconcileNetIncome') ?? 'Adjustments to reconcile Net Income to Net Cash provided by operations:'}
                            </div>
                            {(Operating.Adjustments ?? []).map((item, idx) => this.renderItemRow(item, idx))}
                            {this.renderSectionTotal(
                                translate('text.TotalAdjustments') ?? 'Total Adjustments to reconcile Net Income to Net Cash provided by operations:',
                                Operating.TotalAdjustments
                            )}
                        </>
                    )}
                    <div className="flex items-center justify-between p-2 pl-5 font-bold text-lg bg-tm-gray-100">
                        <div>{translate('text.NetCashFromOperating') ?? 'Net cash provided by operating activities'}:</div>
                        <div>{this.formatMoney(Operating.NetCash ?? 0)}</div>
                    </div>
                </div>

                {/* INVESTING ACTIVITIES */}
                <div className="bg-inverse mt-4">
                    {this.renderSectionHeader(translate('text.InvestingActivities') ?? 'Investing Activities', 'collapseInvesting')}
                    {!this.state.collapseInvesting && (Investing.Items ?? []).map((item, idx) => this.renderItemRow(item, idx))}
                    <div className="flex items-center justify-between p-2 pl-5 font-bold text-lg bg-tm-gray-100">
                        <div>{translate('text.NetCashFromInvesting') ?? 'Net cash provided by investing activities'}:</div>
                        <div>{this.formatMoney(Investing.NetCash ?? 0)}</div>
                    </div>
                </div>

                {/* FINANCING ACTIVITIES */}
                <div className="bg-inverse mt-4">
                    {this.renderSectionHeader(translate('text.FinancingActivities') ?? 'Financing Activities', 'collapseFinancing')}
                    {!this.state.collapseFinancing && (Financing.Items ?? []).map((item, idx) => this.renderItemRow(item, idx))}
                    <div className="flex items-center justify-between p-2 pl-5 font-bold text-lg bg-tm-gray-100">
                        <div>{translate('text.NetCashFromFinancing') ?? 'Net cash provided by financing activities'}:</div>
                        <div>{this.formatMoney(Financing.NetCash ?? 0)}</div>
                    </div>
                </div>

                {/* SUMMARY */}
                <div className="mt-6">
                    <div className="flex items-center justify-between p-2 pl-5 font-bold text-lg border-y border-tm-gray-300">
                        <div>{translate('text.NetCashIncreaseForPeriod') ?? 'NET CASH INCREASE FOR PERIOD'}</div>
                        <div>{this.formatMoney(data.NetCashIncrease ?? 0)}</div>
                    </div>
                    <div className="flex items-center justify-between p-2 pl-5 font-bold">
                        <div>{translate('text.CashAtBeginningOfPeriod') ?? 'Cash at beginning of period'}</div>
                        <div>{this.formatMoney(data.CashAtStart ?? 0)}</div>
                    </div>
                    <div className="flex items-center justify-between p-2 pl-5 font-bold text-lg bg-tm-gray-100 border-y border-tm-gray-300">
                        <div>{translate('text.CashAtEndOfPeriod') ?? 'CASH AT END OF PERIOD'}</div>
                        <div>{this.formatMoney(data.CashAtEnd ?? 0)}</div>
                    </div>
                </div>

                {this.props.resource?.isLoading && <LoaderSmall/>}
            </Card>
        )
    }
}
